/**
 * XableAI Personal Mode V1
 *
 * A person-owned AI workspace where users can:
 * - Own their work history
 * - Carry their AI memory
 * - Let an agent safely propose actions
 *
 * Governance remains under the hood.
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, Navigate } from 'react-router-dom';
import { PersonalLandingPage } from './components/PersonalLandingPage';
import { PersonalDashboard } from './components/PersonalDashboard';
import { PersonalSignIn } from './components/PersonalSignIn';
import { PublicVerifierSurface } from './components/PublicVerifier/PublicVerifierSurface';
import { PublicSignalUI } from './components/publicSignal/PublicSignalUI';
import { XableSystemLandingPage } from '../pages/XableSystemLandingPage';
import { AlertCircle, RefreshCw } from 'lucide-react';
import '../index.css';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-gray-800/50 border border-gray-700 rounded-xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-6">
              We encountered an unexpected error. Please refresh the page or try again later.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh Page</span>
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const handleSignIn = () => {
    setIsAuthenticated(true);
    setShowSignIn(false);
  };

  const handleSignOut = () => {
    setIsAuthenticated(false);
  };

  return (
    <BrowserRouter>
      <AppRouter isAuthenticated={isAuthenticated} showSignIn={showSignIn} isDemoMode={isDemoMode} setIsDemoMode={setIsDemoMode} onSignIn={handleSignIn} onSignOut={handleSignOut} onSignInOpen={() => setShowSignIn(true)} onSignInClose={() => setShowSignIn(false)} />
    </BrowserRouter>
  );
};

const AppRouter: React.FC<{
  isAuthenticated: boolean;
  showSignIn: boolean;
  isDemoMode: boolean;
  setIsDemoMode: (value: boolean) => void;
  onSignIn: () => void;
  onSignOut: () => void;
  onSignInOpen: () => void;
  onSignInClose: () => void;
}> = ({ isAuthenticated, showSignIn, isDemoMode, setIsDemoMode, onSignIn, onSignOut, onSignInOpen, onSignInClose }) => {
  const [searchParams] = useSearchParams();
  
  const isLocalStaging = typeof window !== 'undefined' && (window as any).XABLEAI_ENV === 'local';

  useEffect(() => {
    const demoParam = searchParams.get('demo');
    setIsDemoMode(demoParam === 'true');
  }, [searchParams, setIsDemoMode]);

  return (
    <>
      {isLocalStaging && (
        <div className="fixed top-0 left-0 right-0 bg-orange-600 text-white text-center py-2 px-4 z-50 text-sm font-medium">
          <span className="font-bold">Local staging</span> — Read-only / proposal-only
        </div>
      )}
      <PersonalSignIn
        isOpen={showSignIn}
        onClose={onSignInClose}
        onSuccess={onSignIn}
        isDemoMode={isDemoMode}
      />
      <Routes>
        <Route path="/verify" element={<PublicVerifierSurface />} />
        <Route path="/signals" element={<PublicSignalUI />} />
        <Route path="/system" element={<XableSystemLandingPage />} />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <PersonalDashboard onSignOut={onSignOut} isDemoMode={isDemoMode} />
            ) : (
              <PersonalLandingPage onSignIn={onSignInOpen} isDemoMode={isDemoMode} />
            )
          }
        />
        <Route
          path="/workspace"
          element={
            isAuthenticated ? (
              <PersonalDashboard onSignOut={onSignOut} isDemoMode={isDemoMode} />
            ) : (
              <PersonalLandingPage onSignIn={onSignInOpen} isDemoMode={isDemoMode} />
            )
          }
        />
        <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;
